const safeCurrency = (value) =>
  `${value || ""}`.trim().toUpperCase().slice(0, 12) || null;

const safeFractionDigits = (value, fallback = null) => {
  const number = Number(value);
  if (!Number.isInteger(number) || number < 0 || number > 6) return fallback;
  return number;
};

export const normalizeDisplayCurrency = (value = {}) => {
  if (!value || typeof value !== "object") return null;
  const baseCurrency = safeCurrency(
    value.baseCurrency || value.base_currency,
  );
  const currency = safeCurrency(value.currency);
  const fractionDigits = safeFractionDigits(
    value.fractionDigits ?? value.fraction_digits,
  );
  const baseFractionDigits = safeFractionDigits(
    value.baseFractionDigits ?? value.base_fraction_digits,
  );
  const rate = Number(value.rate);
  const roundUpTo = Number(value.roundUpTo ?? value.round_up_to ?? 1);
  if (
    !baseCurrency ||
    !currency ||
    fractionDigits === null ||
    baseFractionDigits === null ||
    !Number.isFinite(rate) ||
    rate <= 0
  )
    return null;
  return {
    baseCurrency,
    baseFractionDigits,
    currency,
    fractionDigits,
    rate,
    converted: value.converted === true,
    available: value.available !== false,
    requestedCurrency: safeCurrency(
      value.requestedCurrency || value.requested_currency,
    ),
    roundingEnabled:
      value.roundingEnabled === true || value.rounding_enabled === true,
    roundUpTo:
      Number.isFinite(roundUpTo) && roundUpTo > 0 ? roundUpTo : 1,
  };
};

export const normalizeMoneyValue = (
  value,
  { currency = null, fractionDigits = null } = {},
) => {
  if (value && typeof value === "object") {
    const amountMinor = Number(value.amountMinor ?? value.amount_minor);
    const digits = safeFractionDigits(
      value.fractionDigits ?? value.fraction_digits,
      safeFractionDigits(fractionDigits),
    );
    if (!Number.isSafeInteger(amountMinor) || digits === null) return null;
    return {
      amountMinor,
      currency: safeCurrency(value.currency || currency),
      fractionDigits: digits,
    };
  }
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const moneyToMajor = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value && typeof value === "object") {
    const normalized = normalizeMoneyValue(value);
    return normalized
      ? normalized.amountMinor / 10 ** normalized.fractionDigits
      : null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getMoneyCurrency = (value, fallback = null) =>
  (value && typeof value === "object" && safeCurrency(value.currency)) ||
  safeCurrency(fallback);

export const getMoneyFractionDigits = (value, fallback = null) =>
  value && typeof value === "object"
    ? safeFractionDigits(value.fractionDigits ?? value.fraction_digits, fallback)
    : safeFractionDigits(fallback);

const roundConvertedAmount = (amount, displayCurrency) => {
  if (!displayCurrency.roundingEnabled)
    return Number(amount.toFixed(2));
  const inverseStep = 1 / displayCurrency.roundUpTo;
  // Match the legacy roundMe policy while avoiding a floating-point residue
  // turning an already exact step into the next step.
  return (
    Math.ceil(amount * inverseStep - Number.EPSILON * 8) / inverseStep
  );
};

/**
 * Convert an authoritative base Money object for presentation only.
 *
 * Plain numeric values are assumed to already be storefront-display values;
 * this prevents legacy cart/menu prices from being converted twice.
 */
export const convertMoneyForDisplay = (
  value,
  displayCurrencyValue,
  { forceBaseNumeric = false } = {},
) => {
  const displayCurrency = normalizeDisplayCurrency(displayCurrencyValue);
  if (!displayCurrency || !displayCurrency.converted) return value;
  const normalized = normalizeMoneyValue(value);
  const sourceCurrency = getMoneyCurrency(value);
  const baseAmount = moneyToMajor(value);
  const isBaseMoney = Boolean(
    normalized && sourceCurrency === displayCurrency.baseCurrency,
  );
  if (
    baseAmount === null ||
    (!isBaseMoney && !(forceBaseNumeric && typeof value !== "object"))
  )
    return value;
  const converted = roundConvertedAmount(
    baseAmount * displayCurrency.rate,
    displayCurrency,
  );
  const scale = 10 ** displayCurrency.fractionDigits;
  const amountMinor = Math.round(converted * scale);
  if (!Number.isSafeInteger(amountMinor)) return null;
  return {
    amountMinor,
    currency: displayCurrency.currency,
    fractionDigits: displayCurrency.fractionDigits,
  };
};
